<template>
  <div class="content-region">
    <div class="notifications-heading d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-baseline mt-n1">
        <div class="text-gradient d-inline-block text-extra-large" role="heading">Inbox</div>
        <tabs
          :tab-spacing="1"
          class="ml-2 text-medium-1"
          :items="isUnreadFilters"
          v-model="readFilterTabIndex"
        />
      </div>
      <div class="d-flex align-items-center action-links">
        <link-button
          class="text-medium-1"
          @click="togglePreview"
          underline
          v-if="notifications && notifications.length > 0"
          >{{ showPreview ? 'Hide' : 'Show' }} Preview</link-button
        >
        <link-button
          class="text-medium-1 px-0"
          v-if="hasUnreadNotifications"
          @click="markAllAsRead"
          :disabled="readingAll"
          underline
          >Read All</link-button
        >
      </div>
    </div>
    <div v-if="!notifications && isFetching">
      <notification-skeleton class="mb-3" />
    </div>
    <div class="pt-4 pb-5 flex-center" v-else-if="notifications && notifications.length === 0">
      <div class="text-center">
        <i class="bell-empty-icon"></i>
        <div class="text-medium-1 text-secondary">No notifications</div>
      </div>
    </div>
    <div class="pb-5">
      <infinite-scroll
        class="notifications-list"
        @bottom="loadMore"
        :has-more-items="hasMoreNotifications"
        v-if="notifications"
      >
        <div>
          <template v-for="notification in notifications">
            <award-notification
              v-if="notification.type === NOTIFICATION_TYPE.AWARD"
              :key="'unread-' + notification.id"
              :class="{ selected: selected === notification.id }"
              class="notification-item"
              :notification="notification"
              :show-preview="showPreview"
              @notification-click="$emit('notification-click', $event)"
            />
            <mention-notification
              :key="'unread-' + notification.id"
              class="notification-item"
              :class="{ selected: selected === notification.id }"
              :notification="notification"
              :show-preview="showPreview"
              @notification-click="$emit('notification-click', $event)"
              v-else-if="isMentionNotification(notification.type)"
            />
            <reaction-notification
              :key="'unread-' + notification.id"
              class="notification-item"
              :class="{ selected: selected === notification.id }"
              :notification="notification"
              :show-preview="showPreview"
              @notification-click="$emit('notification-click', $event)"
              v-else-if="notification.type === NOTIFICATION_TYPE.REACTION"
            />
          </template>
        </div>
      </infinite-scroll>
    </div>
  </div>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton.vue'
import InfiniteScroll from '@/components/InfiniteScroll'
import Tabs from '@/components/Tabs.vue'
import { notificationMutation } from '@/constants/mutations'
import getQueryParam from '@/utils/getQueryParam'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'
import AwardNotification from '@/components/notifications/AwardNotification'
import MentionNotification from '@/components/notifications/MentionNotification'
import NotificationSkeleton from '@/components/notifications/NotificationSkeleton'
import ReactionNotification from '@/components/notifications/ReactionNotification'
import { NOTIFICATION_TYPE } from '@/constants/notifications'
import { mapActions } from 'vuex'

/**
 * This shows the list of notifications for prop - OrgId
 * @emits:
 * - notification-click: When notification is clicked
 */
export default {
  name: 'NotificationsList',
  props: ['orgId', 'selected'],
  components: {
    Tabs,
    LinkButton,
    MentionNotification,
    NotificationSkeleton,
    AwardNotification,
    ReactionNotification,
    InfiniteScroll
  },
  data() {
    const isUnreadFilterApplied = R.pathOr(
      false,
      ['notificationCenter', this.orgId, 'notifications', 'filter', 'isUnread'],
      this.$store.state.notification
    )
    return {
      isFetching: true,
      readingAll: false,
      NOTIFICATION_TYPE,
      showPreview: false,
      isUnreadFilters: ['All', 'Unread'],
      readFilterTabIndex: Number(isUnreadFilterApplied)
    }
  },
  computed: {
    hasMoreNotifications() {
      const nextUrl = R.path(
        ['notificationCenter', this.orgId, 'notifications', 'next'],
        this.$store.state.notification
      )
      return Boolean(nextUrl)
    },
    notifications() {
      return R.path(
        ['notificationCenter', this.orgId, 'notifications', 'results'],
        this.$store.state.notification
      )
    },
    notificationSummary() {
      return R.pathOr(
        {},
        ['notificationCenter', this.orgId, 'summary'],
        this.$store.state.notification
      )
    },
    hasUnreadNotifications() {
      return this.notificationSummary?.count > 0
    }
  },
  methods: {
    ...mapActions('notification', ['getNotificationList', 'readAllNotifications']),
    togglePreview() {
      this.showPreview = !this.showPreview
    },
    isMentionNotification(type) {
      const mentionNotificationType = [
        NOTIFICATION_TYPE.GOAL_COMMENT_MENTION,
        NOTIFICATION_TYPE.KR_COMMENT_MENTION,
        NOTIFICATION_TYPE.INITIATIVE_COMMENT_MENTION,
        NOTIFICATION_TYPE.INITIATIVE_DESCRIPTION_MENTION
      ]
      return mentionNotificationType.includes(type)
    },
    async markAllAsRead() {
      try {
        this.readingAll = true
        await this.readAllNotifications({
          orgId: this.orgId,
          tillId: this.notifications[0].id
        })
        this.$store.commit(`notification/${notificationMutation.SET_NOTIFICATIONS_LIST}`, {
          orgId: this.orgId
        })
        this.isFetching = true
        await this.getNotificationList({
          orgId: this.orgId,
          query: { unread_summary: true }
        })
        this.readFilterTabIndex = 0
      } catch (e) {
        showAPIError(e)
      }
      this.readingAll = false
      this.isFetching = false
    },
    async fetchNotifications(getUnreadSummary, fetchNext) {
      const nextUrl = R.path(
        ['notificationCenter', this.orgId, 'notifications', 'next'],
        this.$store.state.notification
      )
      const cursor = fetchNext && nextUrl && getQueryParam(nextUrl, 'cursor')
      const query = {}
      if (getUnreadSummary) {
        query.unread_summary = true
      }
      if (this.readFilterTabIndex === 1) {
        query.is_unread = true
      }
      if (cursor) {
        query.cursor = cursor
      }
      this.isFetching = true
      try {
        await this.getNotificationList({
          orgId: this.orgId,
          query,
          appendToExistingList: !!fetchNext
        })
      } catch (e) {
        showAPIError(e)
      }
      this.isFetching = false
    },
    loadMore() {
      if (!this.isFetching && this.hasMoreNotifications) {
        this.fetchNotifications(null, true)
      }
    }
  },
  mounted() {
    if (!this.notifications) {
      this.fetchNotifications(true)
    } else {
      this.isFetching = false
    }
  },
  watch: {
    readFilterTabIndex() {
      this.fetchNotifications(true)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.notification-item {
  cursor: pointer;
  padding: 0px 10px 0 20px;

  /deep/ .right-section {
    padding-bottom: 15px;
  }

  /deep/ .right-section,
  /deep/ .left-section {
    padding-top: 15px;
  }

  &:not(:first-child) {
    /deep/ .right-section {
      border-top: 1px solid $borderPrimary;
    }
  }

  &:hover,
  &.selected {
    background-color: $quinary;

    /deep/ .right-section,
    + .notification-item /deep/ .right-section {
      border-top-color: transparent;
    }

    /deep/ .reaction-icon-wrapper {
      background-color: $selectionColor;
    }
  }
}
.initiative-status {
  margin-left: 20px;
}

.notifications-heading .heading {
  font-weight: 700;
}

.notifications-heading {
  padding: 24px 6px 10px 24px;
  position: sticky;
  top: 0;
  background-color: $documentColor;
  z-index: 1;
}
.content-region {
  container-type: inline-size;
}
@container (max-width: 370px) {
  .notifications-list {
    .left-section i {
      display: none;
    }
    .notification-icon-wrapper {
      width: auto;
    }
  }
  .action-links {
    display: none !important;
  }
}
</style>
